import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, FormTextarea } from "shards-react";
import { HpButton } from "../HpButton/HpButton";
import API from "../Api/API";
import HpModal from "../Modal/HpModal";
import { handleErrorMessage } from "../ErrorMessages/ErrorMessages";
import { showNotification } from "../Notification/Notification";
import CoachSelectForm from "./CoachSelectForm";

const CoachContactForm = ({ primaryColor, open, toggle }) => {
  const { t } = useTranslation(["coach"]);

  const [coach, setCoach] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [step, setStep] = useState("contact"); // "contact" | "select"
  const [pendingCoach, setPendingCoach] = useState(null);

  useEffect(() => {
    fetchCoach().then((c) => setCoach(c));
  }, []);

  useEffect(() => {
    if (!open) {
      setStep("contact");
      setPendingCoach(null);
    }
  }, [open]);

  const fetchCoach = () =>
    axios
      .get(API.url + "api/club/coach", { withCredentials: true })
      .then((res) => res.data)
      .catch((err) => handleErrorMessage(err.response.status));

  const primaryColorBg = {
    backgroundColor: primaryColor,
    borderColor: primaryColor,
  };
  const primaryColorText = { color: primaryColor };
  const outlinePrimary = {
    borderColor: primaryColor,
    color: primaryColor,
    backgroundColor: "transparent",
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setDisabled(true);
    const text = document.getElementById("support_message_input").value;
    if (!text) {
      setDisabled(false);
      return;
    }
    axios
      .post(
        API.url + "api/mail/coach/send",
        { text },
        { withCredentials: true },
      )
      .then(() => {
        toggle();
        setDisabled(false);
        showNotification(t("message_sent_successfully"));
      })
      .catch((error) => handleErrorMessage(error.response.status));
  };

  const confirmCoachSelection = () => {
    if (!pendingCoach) return;

    const doJoin = () =>
      axios
        .post(
          API.url + "api/squad/join/" + pendingCoach.squadId,
          {},
          { withCredentials: true },
        )
        .then(() => {
          showNotification(t(coach ? "coach_changed" : "coach_selected"));
          setCoach(pendingCoach);
          setPendingCoach(null);
          setStep("contact");
        })
        .catch((err) => handleErrorMessage(err.response.status));

    if (coach) {
      axios
        .post(
          API.url + "api/squad/leave/" + coach.squadId,
          {},
          { withCredentials: true },
        )
        .then(doJoin)
        .catch((err) => handleErrorMessage(err.response.status));
    } else {
      doJoin();
    }
  };

  const enterSelectStep = () => {
    setPendingCoach(null);
    setStep("select");
  };

  const title =
    step === "select" ? (
      <span className="d-flex align-items-center gap-2">
        <span
          style={{ cursor: "pointer", fontSize: "0.75em" }}
          onClick={() => setStep("contact")}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </span>
        {t("select_coach")}
      </span>
    ) : (
      t("coach_contact_text")
    );

  const body = (
    <>
      <div style={{ display: step === "contact" ? "block" : "none" }}>
        {coach ? (
          <Form id="coach-contact-form" onSubmit={handleSubmit}>
            <h3 style={primaryColorText}>
              {coach.firstName} {coach.lastName}
            </h3>
            <FormTextarea
              id="support_message_input"
              placeholder={t("message")}
              style={{ minHeight: "86pt" }}
            />
          </Form>
        ) : (
          <h4 style={primaryColorText}>{t("no_coach")}</h4>
        )}
      </div>
      <div style={{ display: step === "select" ? "block" : "none" }}>
        <CoachSelectForm
          primaryColor={primaryColor}
          currentCoach={coach}
          pendingCoach={pendingCoach}
          onPendingSelect={setPendingCoach}
        />
      </div>
    </>
  );

  const actions =
    step === "contact" ? (
      <>
        <HpButton squared outlined onClick={enterSelectStep}>
          {t(coach ? "change_coach" : "select_coach")}
        </HpButton>
        {coach && (
          <HpButton
            form="coach-contact-form"
            type="submit"
            squared
            disabled={disabled}
          >
            {t("send")}
          </HpButton>
        )}
      </>
    ) : (
      <>
        <HpButton onClick={toggle} outlined squared>
          {t("close")}
        </HpButton>
        <HpButton
          squared
          disabled={!pendingCoach}
          onClick={confirmCoachSelection}
        >
          {t("confirm")}
        </HpButton>
      </>
    );

  return (
    <HpModal
      open={open}
      toggle={toggle}
      title={title}
      body={body}
      actions={actions}
    />
  );
};

export default CoachContactForm;
